// MyContext.tsx
import {
  MallsMall,
  MallsCartConfig,
  MallsAccumulationConfig,
  CategoriesResponse,
  MultiLevelCategory,
  FlatCategory,
} from '@shopby/shop-sdk'
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react'
import { isMobile } from 'react-device-detect'
import { find, some } from 'lodash'
import { getMall, getCategories } from '../../api/product'
import { sendSetAppsflyerEvent } from '../../js/postMessage'

export type CategoryDepth = {
  [key: string]: MultiLevelCategory
}

export type ExtendsCategories = CategoriesResponse & {
  categoryDepth?: CategoryDepth
}

interface MyContextProps {
  isToast: boolean
  setIsToast: React.Dispatch<React.SetStateAction<boolean>>
  toastMessage: string
  setToastMessage: React.Dispatch<React.SetStateAction<string>>
  toastBtnMessage: string
  setToastBtnMessage: React.Dispatch<React.SetStateAction<string>>
  toastEvent: () => void
  setToastEvent: React.Dispatch<React.SetStateAction<() => void>>
  malls: MallsMall | null
  cartConfig: MallsCartConfig | null
  accumulationConfig: MallsAccumulationConfig | null
  categories: CategoriesResponse | null
  findCategory: (categories: CategoriesResponse, categoryId: number) => ExtendsCategories | null
  scrollHeader: React.MutableRefObject<boolean>
  setScrollHeader: (value: boolean) => void
}

const defaultContext: MyContextProps = {
  isToast: false,
  setIsToast: () => {},
  toastMessage: '',
  setToastMessage: () => {},
  toastBtnMessage: '',
  setToastBtnMessage: () => {},
  toastEvent: () => {},
  setToastEvent: () => {},
  malls: null,
  cartConfig: {
    storagePeriod: 0,
    storageMaxQuantity: 100,
    cartEquivalentOptionUnitType: '',
    storagePeriodNoLimit: true,
  },
  accumulationConfig: null,
  categories: {} as CategoriesResponse,
  findCategory: () => null,
  scrollHeader: { current: false },
  setScrollHeader: () => {},
}

const MyContext = createContext<MyContextProps>(defaultContext)

interface MyContextProviderProps {
  children: ReactNode
}

const MyProvider: React.FC<MyContextProviderProps> = ({ children }) => {
  const [isToast, setIsToast] = useState<boolean>(false)
  const [toastMessage, setToastMessage] = useState<string>('')
  const [toastBtnMessage, setToastBtnMessage] = useState<string>('')
  const [toastEvent, setToastEvent] = useState<() => void>(() => {})
  const [malls, setMalls] = useState<MallsMall | null>(null)
  const [cartConfig, setCartConfig] = useState<MallsCartConfig | null>(null)
  const [accumulationConfig, setAccumulationConfig] = useState<MallsAccumulationConfig | null>(null) // 적립금 설정은 사용하지 않음
  const [categories, setCategories] = useState<CategoriesResponse | null>(null)
  const scrollHeader = useRef<boolean>(false)
  const position = useRef<number>(window.scrollY)

  // 멀티레벨 카테고리 찾기
  const findInMultiLevel = useCallback(
    (
      categories: MultiLevelCategory[],
      categoryId: number,
      depth: number = 1,
      categoryDepth: CategoryDepth = {}
    ): { category: MultiLevelCategory | null; categoryDepth: any } => {
      const category = find(categories, (cat) => cat.categoryNo === categoryId)

      if (category) {
        categoryDepth[`depth${depth}`] = category
        return { category, categoryDepth }
      }

      for (const cat of categories) {
        if (cat.children) {
          const found = findInMultiLevel(cat.children, categoryId, depth + 1, categoryDepth)
          if (found.category) {
            categoryDepth[`depth${depth}`] = cat
            return {
              category: {
                ...cat,
                children: [found.category],
              },
              categoryDepth: found.categoryDepth,
            }
          }
        }
      }

      return { category: null, categoryDepth }
    },
    []
  )

  // 플랫 카테고리 찾기
  const findInFlat = useCallback((categories: FlatCategory[] | undefined, categoryId: number): FlatCategory[] => {
    if (!categories) return []
    return categories.filter((category) =>
      some(
        [
          category.depth1CategoryNo,
          category.depth2CategoryNo,
          category.depth3CategoryNo,
          category.depth4CategoryNo,
          category.depth5CategoryNo,
        ],
        (depthCategoryNo) => depthCategoryNo === categoryId
      )
    )
  }, [])

  // 카테고리 찾기
  const findCategory = useCallback(
    (categories: ExtendsCategories | null, categoryId: number) => {
      if (!categories) return null
      const { multiLevelCategories, flatCategories } = categories
      if (!multiLevelCategories || !flatCategories) return null
      const { category: multiLevelCategory, categoryDepth } = findInMultiLevel(multiLevelCategories, categoryId)
      const flatCategory = findInFlat(flatCategories, categoryId)

      return { multiLevelCategory, flatCategory, categoryDepth }
    },
    [findInMultiLevel, findInFlat]
  )

  const toggleMultipleClasses = useCallback((elements: (Element | null)[], className: string, condition: boolean) => {
    elements.forEach((element) => {
      if (element) element.classList.toggle(className, condition)
    })
  }, [])

  const handleScroll = useCallback(() => {
    const elements = {
      logo: document.querySelector('.header_wrap.home .logo_nav_wrap'),
      headerWrap: document.querySelector('.header_wrap.home'),
      iconContainer: document.querySelector('.header_wrap.home .search_icon_wrap'),
      logoApp: document.querySelector('.header_res_wrap .logo_nav_wrap'),
      headerWrapApp: document.querySelector('.header_res_wrap'),
      iconContainerApp: document.querySelector('.header_res_wrap .search_icon_wrap'),
      logoPc: document.querySelector('.header_wrap.pc .logo_nav_wrap'),
      headerWrapPc: document.querySelector('.header_wrap.pc'),
      iconContainerPc: document.querySelector('.header_wrap.pc .search_icon_wrap'),
      categoryPC: document.querySelector('.on_pc_category'),
      categoryMenu: document.querySelector('.category_header'),
      categoryMenuFour: document.querySelector('.category_header4'),
      categoryMenuPc: document.querySelector('.category_header.pc'),
      categoryMenuFourPc: document.querySelector('.category_header4.pc'),
      mainWrap: document.querySelector('.main_wrap'),
      exhibitioncategoryMenu: document.querySelector('.exhibition_category_header'),
      exhibitioncategoryMenuFour: document.querySelector('.exhibition_category_header4'),
      exhibitioncategoryMenuFourSubmenu: document.querySelector('.exhibition_category_header_four_submenu'),
      exhibitionVisual: document.querySelector('.visual_img'),
      eventTopHeight: document.querySelector('.event_top')?.clientHeight || 0,
      eventWrapHeight: document.querySelector('.event_wrap')?.clientHeight || 0,
      exhibitionDetailContHeight: document.querySelector('.exhibition_detail_cont')?.clientHeight || 0,
      anchorTab: document.querySelector('.anchor_tab'),
      eventWrap: document.querySelector('.event_wrap'),
    }

    const is_scroll = window.scrollY
    const isBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight
    const exhibitionVisualHeight = elements.exhibitionVisual?.clientHeight || 0

    const mobileHeaderCondition = is_scroll > 30
    toggleMultipleClasses(
      [
        elements.logo,
        elements.headerWrap,
        elements.iconContainer,
        elements.logoApp,
        elements.headerWrapApp,
        elements.iconContainerApp,
      ],
      'on',
      mobileHeaderCondition
    )

    // pc 웹 헤더
    const pcHeaderCondition = is_scroll > 0
    toggleMultipleClasses([elements.headerWrapPc], 'pc_on', pcHeaderCondition)
    scrollHeader.current = pcHeaderCondition

    // mobile 앱 카테고리
    const mobileAppCategoryCondition = location.pathname.includes('festivals')
    const isScrollDown = is_scroll > position.current
    const exhibitionFixedCondition =
      !isBottom && is_scroll <= elements.eventWrapHeight + elements.eventTopHeight - elements.exhibitionDetailContHeight
    const exhibitionVisualCondition = !isBottom && is_scroll <= exhibitionVisualHeight + 14

    const updateExhibitionCategoryMenu = (condition: boolean) => {
      const elementsToToggle = [
        elements.exhibitioncategoryMenu,
        elements.exhibitioncategoryMenuFour,
        elements.exhibitioncategoryMenuFourSubmenu,
      ]

      toggleMultipleClasses(elementsToToggle, 'fixed', elements.anchorTab ? false : condition)
    }

    const condition = mobileAppCategoryCondition
      ? !isScrollDown && exhibitionFixedCondition
      : isScrollDown && !exhibitionVisualCondition

    updateExhibitionCategoryMenu(condition)

    // mobile 웹 카테고리
    const mobileWebCategoryCondition = is_scroll > 64
    toggleMultipleClasses(
      [elements.categoryMenu, elements.categoryMenuFour],
      'fixed',
      !isScrollDown && mobileWebCategoryCondition && !isBottom
    )

    // pc 웹 카테고리
    const pcWebCategoryCondition = is_scroll > 138
    toggleMultipleClasses(
      [elements.categoryMenuPc, elements.categoryMenuFourPc],
      'fixed',
      !isScrollDown && pcWebCategoryCondition && !isBottom
    )

    position.current = is_scroll
  }, [toggleMultipleClasses])

  useEffect(() => {
    // 기획전 상세 헤더 예외 처리
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories()
        setCategories(response.data)
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }

    const fetchMallData = async () => {
      try {
        const response = await getMall()
        setMalls(response.data.mall)
        setCartConfig(response.data.cartConfig)
        setAccumulationConfig(response.data.accumulationConfig)
      } catch (error) {
        console.error('Error fetching mall data:', error)
      }
    }

    fetchCategories()
    fetchMallData()
  }, [])

  useEffect(() => {
    if (window.ReactNativeWebView) {
      const path = location.pathname + location.search
      sendSetAppsflyerEvent({
        eventName: 'screenEvent',
        eventType: {
          name: 'About_fishing',
          class: path,
        },
      })
    }
    setIsToast(false)
  }, [location.pathname, location.search])

  useEffect(() => {
    const footer = document.querySelector('.footer_wrap') as HTMLElement
    if (isMobile) {
      if (footer) {
        if (location.pathname === '/') {
          footer.style.paddingBottom = '36px'
        }
      }
    }
    setIsToast(false)
  }, [location.pathname])

  // 초기화
  useEffect(() => {
    const categoryMenuPc = document.querySelector('.category_header.pc')
    const categoryMenuFourPc = document.querySelector('.category_header4.pc')
    categoryMenuPc?.classList.remove('fixed')
    categoryMenuFourPc?.classList.remove('fixed')
  }, [location.pathname])

  const contextValue = React.useMemo(
    () => ({
      isToast,
      setIsToast,
      toastMessage,
      setToastMessage,
      toastBtnMessage,
      setToastBtnMessage,
      toastEvent,
      setToastEvent,
      malls,
      cartConfig,
      accumulationConfig,
      categories,
      findCategory,
      scrollHeader,
      setScrollHeader: (value: boolean) => (scrollHeader.current = value),
    }),
    [
      isToast,
      setIsToast,
      toastMessage,
      setToastMessage,
      toastBtnMessage,
      setToastBtnMessage,
      toastEvent,
      setToastEvent,
      malls,
      cartConfig,
      accumulationConfig,
      categories,
      findCategory,
    ]
  )

  return <MyContext.Provider value={contextValue}>{children}</MyContext.Provider>
}

const useMyContext = () => {
  const context = useContext(MyContext)
  if (!context) {
    throw new Error('useMyContext 가 MyProvider 안에서 사용되지 않았습니다.')
  }
  return context
}

export { MyContext, MyProvider, useMyContext }
